import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { authApi } from '@/api/auth';
import { api, ApiError } from '@/api/client';
import type { CurrentUserDto, UserRole } from '@/api/types';

interface AuthContextValue {
  user: CurrentUserDto | null;
  loading: boolean;
  setupRequired: boolean;
  initializationError: string | null;
  login: (email: string, password: string) => Promise<CurrentUserDto>;
  developmentLogin: (role: UserRole) => Promise<CurrentUserDto>;
  completeSetup: (secret: string, displayName: string, email: string, password: string) => Promise<CurrentUserDto>;
  logout: () => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<CurrentUserDto>;
  refreshUser: () => Promise<void>;
  retryInitialization: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUserDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [setupRequired, setSetupRequired] = useState(false);
  const [initializationError, setInitializationError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const initialize = useCallback(async () => {
    setLoading(true);
    setInitializationError(null);
    try {
      const setup = await authApi.setupStatus();
      setSetupRequired(setup.required);
      if (setup.required) {
        setUser(null);
        queryClient.clear();
        return;
      }

      try {
        setUser(await authApi.me());
      } catch (error: unknown) {
        if (error instanceof ApiError && error.status === 401) {
          setUser(null);
        } else {
          throw error;
        }
      }
    } catch (error: unknown) {
      setInitializationError(error instanceof Error ? error.message : 'Unable to initialize the application.');
    } finally {
      setLoading(false);
    }
  }, [queryClient]);

  useEffect(() => {
    let active = true;
    api.setUnauthorizedHandler(() => {
      if (active) {
        setUser(null);
        queryClient.clear();
      }
    });

    void initialize();

    return () => {
      active = false;
      api.setUnauthorizedHandler(null);
    };
  }, [initialize, queryClient]);

  const value = useMemo<AuthContextValue>(() => ({
    user,
    loading,
    setupRequired,
    initializationError,
    async login(email, password) {
      const currentUser = await authApi.login(email, password);
      queryClient.clear();
      setUser(currentUser);
      return currentUser;
    },
    async developmentLogin(role) {
      const currentUser = await authApi.developmentLogin(role);
      queryClient.clear();
      setUser(currentUser);
      return currentUser;
    },
    async completeSetup(secret, displayName, email, password) {
      try {
        const currentUser = await authApi.completeSetup({ secret, displayName, email, password });
        queryClient.clear();
        setSetupRequired(false);
        setUser(currentUser);
        return currentUser;
      } catch (error) {
        if (error instanceof ApiError && error.status === 409) {
          const setup = await authApi.setupStatus();
          setSetupRequired(setup.required);
        }
        throw error;
      }
    },
    async logout() {
      try {
        await authApi.logout();
      } finally {
        queryClient.clear();
        setUser(null);
      }
    },
    async changePassword(currentPassword, newPassword) {
      const currentUser = await authApi.changePassword(currentPassword, newPassword);
      setUser(currentUser);
      return currentUser;
    },
    async refreshUser() {
      const currentUser = await authApi.me();
      setUser(currentUser);
    },
    retryInitialization: initialize,
  }), [initializationError, initialize, loading, queryClient, setupRequired, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider.');
  return context;
}

export function useOptionalAuth() {
  return useContext(AuthContext);
}
